/**
 * Document view component.
 * @module components/theme/View/ContactView
 */

import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';

import { Container, Segment, Grid, Label } from 'semantic-ui-react';
import config from '@plone/volto/registry';
import { getSchema } from '@plone/volto/actions';
import { getWidget } from '@plone/volto/helpers/Widget/utils';

import { useDispatch, useSelector } from 'react-redux';

/**
 * Component to display the default view.
 * @function ContactView
 * @param {Object} content Content object.
 * @returns {string} Markup of the component.
 */
const ContactView = (props) => {
  const { content, location } = props;
  const dispatch = useDispatch();
  const { views } = config.widgets;
  const contentLoaded = useSelector((state) => state.content.get.loaded);
  const contentSchema = useSelector((state) => state.schema?.schema);
  const fieldsetsToExclude = [
    'categorization',
    'dates',
    'ownership',
    'settings',
  ];
  const fieldsets = contentSchema?.fieldsets.filter(
    (fs) => !fieldsetsToExclude.includes(fs.id),
  );

  React.useEffect(() => {
    content?.['@type'] &&
      dispatch(getSchema(content['@type'], location.pathname));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If the content is not yet loaded, then do not show anything
  return contentLoaded ? (
    <Container id="page-document">
      <h1 className="documentFirstHeading">{content.title}</h1>
      {fieldsets?.map((fs) => {
        return (
          <div className="fieldset" key={fs.id}>
            {fs.id !== 'default' && <h2>{fs.title}</h2>}
            {fs.fields?.map((f, key) => {
              let field = {
                ...contentSchema?.properties[f],
                id: f,
                widget: getWidget(f, contentSchema?.properties[f]),
              };
              let Widget = views?.getWidget(field);
              return f !== 'title' ? (
                <Grid celled="internally" key={key}>
                  <Grid.Row>
                    <Label>{field.title}:</Label>
                  </Grid.Row>
                  <Grid.Row>
                    <Segment basic>
                      <Widget value={content[f]} />
                    </Segment>
                  </Grid.Row>
                </Grid>
              ) : (
                <Widget key={key} value={content[f]} />
              );
            })}
          </div>
        );
      })}
    </Container>
  ) : null;
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
ContactView.propTypes = {
  /**
   * Content of the object
   */
  content: PropTypes.shape({
    /**
     * Title of the object
     */
    title: PropTypes.string,
    /**
     * Description of the object
     */
    description: PropTypes.string,
    /**
     * Text of the object
     */
    text: PropTypes.shape({
      /**
       * Data of the text of the object
       */
      data: PropTypes.string,
    }),
  }).isRequired,
};

export default injectIntl(ContactView);
